import { useState } from 'react';
import { IconEdit, IconPlus } from '@tabler/icons-react';
import { useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './payroll.i18n';
import { ALW_ROWS, ATT_ROWS, KPI_RULES, PAR_ROWS, TAX_ROWS, logRows } from './payrollData';
import s from './payroll.module.css';

// Mint-Payroll: 3 tab còn lại — Tham số & biểu thuế · Phụ cấp · KPI · chuyên cần · Nhật ký gửi phiếu. Chỉ xem; nút ghi báo toast, ô tích giữ trong bộ nhớ.

const Th = ({ cols, dense }: { cols: readonly (readonly [string, string, boolean?])[]; dense?: boolean }) => (
  <thead data-dense={dense ? 1 : undefined}>
    <tr>{cols.map(([h, w, r], i) => <th key={i} scope="col" style={{ width: w }} className={r ? s.r : undefined}>{h}</th>)}</tr>
  </thead>
);

export function ParamTab({ dense }: { dense: boolean }) {
  const t = useT();
  const toast = useToast();
  const [ok, setOk] = useState<Record<string, boolean>>({});
  const noWrite = () => toast({ msg: t('pr.noWrite') });
  return (
    <div className={s.two}>
      <section className={`${s.card} ${s.sec}`} aria-label="Tham số lương">
        <div className={s.secHead}>
          <h2>{t('pr.legal')}</h2>
          <button type="button" className={s.mini} onClick={noWrite}><IconPlus size={16} aria-hidden="true" />{t('pr.newParam')}</button>
        </div>
        <table className={`${s.tbl} ${s.fixed}`} data-dense={dense ? 1 : undefined}>
          <Th cols={[['Tham số', '26%'], ['Giá trị', '19%', true], ['Hiệu lực', '12%'], ['Căn cứ', '21%'], ['Trạng thái', '12%'], ['', '10%', true]]} />
          <tbody>
            {PAR_ROWS.map(([code, name, v, unit, eff, legal, st]) => {
              const sv = ok[code] ? 'Confirmed' : st;
              return (
                <tr key={code}>
                  <td className={s.cut}><b>{name}</b><span>{code}</span></td>
                  <td className={s.r}><b className={s.inl}>{v}</b> <span className={s.inlLb}>{unit}</span></td>
                  <td>{eff}</td>
                  <td className={`${s.cut} ${s.tx2}`} title={legal}>{legal}</td>
                  <td><span className={`${s.st} ${sv === 'Confirmed' ? '' : s.stWarn}`}>{sv}</span></td>
                  <td className={s.r}>
                    {sv === 'NeedVerify' && (
                      <button type="button" className={`${s.mini} ${s.miniBtn}`} onClick={() => { setOk({ ...ok, [code]: true }); toast({ msg: `Đã xác nhận ${code}`, undo: () => setOk((o) => ({ ...o, [code]: false })) }); }}>{t('pr.confirm')}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section className={`${s.card} ${s.side}`} aria-label="Biểu thuế TNCN">
        <div className={s.sideHead}>
          <h2 title="Ngưỡng và thuế suất là số minh hoạ — biểu áp dụng kỳ 2026 chờ anh chốt theo văn bản pháp lý hiện hành.">{t('pr.taxTable')}</h2>
          <button type="button" className={s.mini} onClick={noWrite}><IconEdit size={16} aria-hidden="true" />{t('pr.editTable')}</button>
        </div>
        <div className={s.sideSub}>{t('pr.taxSub')}</div>
        {TAX_ROWS.map(([n, range, rate]) => <div key={n} className={s.tax}><b>Bậc {n}</b><span>{range}</span><b>{rate}</b></div>)}
      </section>
    </div>
  );
}

export function AllowTab({ dense }: { dense: boolean }) {
  const t = useT();
  const toast = useToast();
  const [ov, setOv] = useState<Record<string, { si?: boolean; tx?: boolean }>>({});
  const [att, setAtt] = useState(true);
  const noWrite = () => toast({ msg: t('pr.noWrite') });
  const flip = (code: string, name: string, k: 'si' | 'tx', cur: boolean) => {
    const prev = ov[code];
    setOv({ ...ov, [code]: { ...prev, [k]: !cur } });
    toast({ msg: `${name}: ${k === 'si' ? (!cur ? 'tính' : 'không tính') + ' BHXH' : (!cur ? 'miễn' : 'chịu') + ' thuế'}`, undo: () => setOv((o) => ({ ...o, [code]: { ...prev } })) });
  };
  return (
    <>
      <section className={`${s.card} ${s.sec}`} aria-label="Loại phụ cấp">
        <div className={s.secHead}>
          <h2>{t('pr.allowType')}</h2>
          <button type="button" className={s.mini} onClick={noWrite}><IconPlus size={16} aria-hidden="true" />{t('pr.allowType')}</button>
        </div>
        <table className={`${s.tbl} ${s.fixed}`} data-dense={dense ? 1 : undefined}>
          <Th cols={[['Loại phụ cấp', '24%'], ['Mức chuẩn', '14%', true], ['BHXH', '13%'], ['Thuế', '13%'], ['Ghi chú', '36%']]} />
          <tbody>
            {ALW_ROWS.map(([code, name, v, si0, tx0, note]) => {
              const si = ov[code]?.si ?? si0;
              const tx = ov[code]?.tx ?? tx0;
              return (
                <tr key={code}>
                  <td><b>{name}</b><span>{code}</span></td>
                  <td className={s.r}>{v}</td>
                  <td><label className={s.chk}><input type="checkbox" checked={si} onChange={() => flip(code, name, 'si', si)} />{t('pr.siCalc')}</label></td>
                  <td><label className={s.chk}><input type="checkbox" checked={tx} onChange={() => flip(code, name, 'tx', tx)} />{t('pr.taxFree')}</label></td>
                  <td className={`${s.cut} ${s.tx2}`}>{note}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <div className={s.two}>
        <section className={`${s.card} ${s.sec}`} aria-label="Quy tắc KPI lương">
          <div className={s.secHead}>
            <h2 title="Mức thưởng chờ anh chốt.">{t('pr.kpiRules')}</h2>
            <button type="button" className={s.mini} onClick={noWrite}><IconPlus size={16} aria-hidden="true" />{t('pr.rule')}</button>
          </div>
          <table className={`${s.tbl} ${s.fixed}`} data-dense={dense ? 1 : undefined}>
            <Th cols={[['Chức danh', '17%'], ['Chỉ số', '41%'], ['Cách tính', '16%'], ['Kỳ', '15%'], ['Mức', '11%']]} />
            <tbody>
              {KPI_RULES.map(([title, lvl, metric, src, rate, per]) => (
                <tr key={title}>
                  <td><b>{title}</b><span>{lvl}</span></td>
                  <td className={s.cut}>{metric}<span>{src}</span></td>
                  <td>{rate}</td>
                  <td>{per} · {t('pr.paidNext')}</td>
                  <td style={{ color: 'var(--warn)' }}>{t('pr.tbd')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className={`${s.card} ${s.side}`} aria-label="Thưởng chuyên cần">
          <div className={s.sideHead}>
            <h2>{t('pr.attBonus')}</h2>
            <label className={s.chk} style={{ fontWeight: 'var(--fw-label)' }}>
              <input type="checkbox" checked={att} onChange={() => { setAtt(!att); toast({ msg: att ? 'Tắt thưởng chuyên cần' : 'Bật thưởng chuyên cần · tính lại kỳ đang mở' }); }} />{t('pr.on')}
            </label>
          </div>
          {ATT_ROWS.map(([k, v]) => <div key={k} className={s.kv}><span>{k}</span><b>{v}</b></div>)}
          <span className={`${s.st} ${s.stWarn}`} style={{ alignSelf: 'flex-start', marginTop: 8 }}>{t('pr.attTbd')}</span>
        </section>
      </div>
    </>
  );
}

export function LogTab({ per, dense }: { per: string; dense: boolean }) {
  const t = useT();
  const toast = useToast();
  const [re, setRe] = useState<Record<string, boolean>>({});
  const rows = logRows(per);
  return (
    <section className={`${s.card} ${s.sec}`} aria-label="Nhật ký gửi phiếu lương">
      <table className={`${s.tbl} ${s.fixed}`} data-dense={dense ? 1 : undefined}>
        <Th cols={[['Người · ghế', '22%'], ['Kỳ', '10%'], ['Gửi tới', '24%'], ['Lúc', '18%'], ['Kết quả', '16%'], ['', '10%', true]]} />
        <tbody>
          {rows.map(([who, seat, p, when, st, err]) => {
            const done = re[p + who];
            const sv = done ? 'Đã gửi' : st;
            return (
              <tr key={who}>
                <td><b>{who}</b><span>{seat}</span></td>
                <td>{p}</td>
                <td className={s.tx2}>mail công ty · {who}</td>
                <td>{done ? '22/Oct/2026 09:40' : when}</td>
                <td>
                  <span className={`${s.st} ${sv === 'Lỗi' ? s.stErr : ''}`}>{sv}</span>
                  {err && !done && <div className={s.errTx}>{err}</div>}
                </td>
                <td className={s.r}>
                  {sv === 'Lỗi' && <button type="button" className={s.mini} onClick={() => { setRe({ ...re, [p + who]: true }); toast({ msg: `Đã gửi lại phiếu ${p} cho ${who}` }); }}>{t('pr.resend')}</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!rows.length && <div className={s.empty}>Kỳ {per} chưa gửi phiếu lương nào.</div>}
    </section>
  );
}
